import React, { useSyncExternalStore } from 'react';
import {Box, Button, Typography} from '@mui/material';
import { SP } from '../../theme/tokens';
// UI2 Teil 9, Kriterium 6: im bestaetigten Ausfall schweigt NUR der Fehlerzweig.
import { abonniere, aktuellerZustand, ZUSTAND } from '../../api/serviceOutage';

/**
 * EmptyState — der Leerzustand einer Fläche, mit verpflichtendem Grund.
 *
 * @description
 *   Ersetzt die 86 handgerollten Leerzustände im Repo. Der Unterschied ist
 *   nicht die Form, sondern `reason`: eine Fläche, die nichts zeigt, muss
 *   sagen WARUM — und die Warums sind verschiedene Aussagen mit
 *   verschiedener Tonalität.
 *
 *   🔴 Der Grund ist ein PFLICHT-Prop, kein Vorschlag. Ohne ihn kann ein
 *   Fehler als "nichts da" erscheinen, und genau das passiert im Repo an
 *   mehreren Stellen: ein fehlgeschlagener Abruf zeichnet ein leeres Ergebnis,
 *   ein Netzfehler sieht aus wie "keine Arbeit".
 *
 *   Anlass: `ZeroInboxState.jsx:29` schreibt den Widerspruch selbst hin —
 *   "Either everyone's happy, or the email server is down. Probably the first
 *   one." Die Komponente WEISS, dass sie die beiden Fälle nicht unterscheiden
 *   kann, und macht daraus einen Witz. Der Grund gehört in den Aufruf, nicht
 *   in den Text.
 *
 *   🔴 `data-reason` steht UNBEDINGT im DOM (UI2 Teil 10, G0.2). Es ist an
 *   keine neue Prop gebunden, weil `reason` schon heute Pflicht ist — es gibt
 *   also keinen Zustand "ohne die neuen Props", in dem das Attribut fehlen
 *   könnte. Es ist eine nicht sichtbare DOM-Ergänzung: Text, Rolle und Layout
 *   bleiben gegenüber der Fassung davor unverändert. Es ist die Prüffläche von
 *   P9 (`[data-reason]` je Szenario) und die einzige Stelle, an der eine
 *   Prüfung "welcher Grund steht hier?" ohne Textvergleich beantworten kann.
 *
 * @useWhen
 *   - Eine Liste, Tabelle oder ein Panel ist leer und die Ursache ist bekannt
 *   - Ein Abruf ist fehlgeschlagen und die Fläche braucht einen Wiederholen-Weg
 *   - Eine Filterkombination liefert nichts und der Nutzer soll sie lockern
 *   - Eine Quelle hat noch nicht geliefert — die leere Liste bedeutet nichts
 *
 * @dontUseFor
 *   - Ladezustände — dafür `frontend/src/Skeletons/` (anti-patterns §11)
 *   - Fehler MIT Details und Feldern — dafür die Banner- und Notice-Muster
 *   - Bestätigungen und Erfolgsmeldungen — dafür `sonner`
 *
 * @related
 *   - `ResultCount` — der Zähler daneben; bei null Treffern spricht der Leerzustand, nicht die 0
 *   - `NoticeSlot` — der EINE Hinweisbereich einer Detailseite; er trägt Zustand MIT Handlung
 *   - `ZeroInboxState` — der handgerollte Fall, der diesen Baustein ausgelöst hat
 *     (🔴 abgelöst; gelöscht wird er nach der Migration G4, nicht vorher)
 *   - `RouteFallback` — der Ladezustand, nicht der Leerzustand
 *
 * @reuseHints
 *   - Die 86 handgerollten Leerzustände nach und nach hierher ziehen
 *   - `CommandPalette` ist bisher der einzige Verbraucher — Tabellen und Panels als Nächstes
 *
 * @props
 *   reason        — {'no-data'|'no-match'|'error'|'unknown'} Pflicht. Bestimmt Tonalität und Vorgabetext; ohne ihn wirft die Komponente.
 *   message       — {string} Eigener Text statt des Vorgabetexts.
 *   detail        — {string} Zweite Zeile mit einer Nutzerauskunft, die die `message`
 *                   ergaenzt — nie ein Endpunkt, Filter oder Env-Name (§4.1).
 *   action        — {React.ReactNode} Ein Weg heraus — Wiederholen, Filter leeren.
 *                   🔴 Fuer `reason="error"` ZUGESAGT (siehe @useWhen): ohne ihn sieht
 *                   ein Fehler wie "nichts da" aus. Nicht erzwungen (der Slot ist
 *                   optional), aber im Entwicklungsmodus gemeldet.
 *   query         — {string} Der Suchbegriff. Setzt die Echo-Zeile
 *                   „No <noun> matches “…”." (ohne `noun`: „Nothing matches “…”.").
 *                   🔴 Nur bei `reason="no-match"` (R16-B1): unter „error" behauptet
 *                   ein Echo „nichts gefunden", wo „konnte nicht laden" gilt — die
 *                   falsche Kombination wird im Entwicklungsmodus gemeldet.
 *                   🔴 Nur mit `onClearSearch` — ein Echo ohne Weg heraus ist der
 *                   Zustand, den die Komponente meldet (E.2).
 *                   🔴 Ist `query` gesetzt und KEIN `message`, wird der deutsche
 *                   Vorgabetext UNTERDRUECKT — das Echo ist dann die Hauptzeile
 *                   (R16-B2: eine Zeile, eine Sprache, kein Doppelsatz).
 *   noun          — {string} Das Objekt des Echo-Satzes, EINZAHL („case" →
 *                   „No case matches “kaffee”."). Ohne ihn der neutrale Satz
 *                   „Nothing matches “…”." — Konzept §8.1. Nur mit `query` wirksam.
 *   searchedIn    — {string} "Searched in: …" — WO gesucht wurde. Nennt die Flaechen,
 *                   nie einen Endpunkt. Nur bei `reason="no-match"` (R16-B1).
 *   onClearSearch — {Function} Standardhandlung "Clear search".
 *   onClearFilters— {Function} Standardhandlung "Clear all filters".
 *   onRetry       — {Function} Standardhandlung "Try again".
 *   showDuringOutage — {boolean} OPT-IN, Vorgabe `false`. Laesst `reason="error"`
 *                   AUCH im bestaetigten Ausfall stehen, statt zu schweigen
 *                   (R16-W2-1 / R15-W1, Nachbesserung 2, 2026-10-15). Fuer eine
 *                   Flaeche, die den Ausfall SELBST meldet, weil der globale
 *                   Banner sie nicht erreicht — er liegt bewusst UNTER jedem
 *                   Modal (`ServiceOutageBanner.jsx` `bannerEbene`). Ohne diese
 *                   Prop bleibt das Verhalten Zeichen fuer Zeichen das alte:
 *                   der Fehlerzweig schweigt, der Banner traegt die Aussage.
 */
export default function EmptyState({
  reason,
  message,
  detail,
  action,
  query,
  noun,
  searchedIn,
  onClearSearch,
  onClearFilters,
  onRetry,
  showDuringOutage = false,
}) {
  // 🔴 R16-B2 (Nachbesserung 4, 2026-10-11): ABONNIERT, nicht gelesen.
  //
  // Hier stand `istAusfall()` — ein Renderzeit-Entscheid OHNE Abonnement. Der
  // Provider wird in `App.js` nur um den Banner montiert, eine Zustandsaenderung
  // rendert also NUR den Banner neu. In der NATUERLICHEN Reihenfolge — der Abruf
  // scheitert (Zustand noch `online`), die Flaeche rendert ihren Fehler, DANN
  // bestaetigt die Sonde den Ausfall — blieb der Fehlertext damit stehen; die
  // Ausblendung griff nur, wenn der Ausfall schon VOR dem Rendern stand (genau
  // die umgekehrte Reihenfolge, die die alte Probe fuhr).
  //
  // `useSyncExternalStore` haengt die Komponente direkt an den Modulzustand: sie
  // rendert bei jedem Wechsel neu, mit und ohne Provider. Die drei Argumente
  // sind stabil (`abonniere`/`aktuellerZustand` sind Modulfunktionen), also
  // entsteht keine Endlosschleife. Der Hook steht VOR den Wuerfen — Haken
  // duerfen nicht hinter einem `return`/`throw` liegen.
  const zustand = useSyncExternalStore(abonniere, aktuellerZustand, aktuellerZustand);

  if (!reason) {
    // Bewusst laut: ein Leerzustand ohne Grund ist die Ursache, nicht das
    // Ergebnis. Siehe @description.
    throw new Error(
      'EmptyState braucht einen `reason` — "no-data", "no-match", "error" oder "unknown". ' +
      'Ohne ihn kann ein Fehler als "nichts da" erscheinen.'
    );
  }
  const KNOWN = ['no-data', 'no-match', 'error', 'unknown'];
  if (!KNOWN.includes(reason)) {
    throw new Error(`EmptyState: unbekannter reason "${reason}". Erlaubt: ${KNOWN.join(', ')}.`);
  }

  // UI2 Teil 9, Z-1a (Kriterium 6): im bestaetigten Ausfall schweigt NUR der
  // Fehlerzweig — der globale Banner traegt die Aussage. `no-data`/`no-match`
  // bleiben sichtbar, sie sind keine Fehler. Die Flaeche VERSCHWINDET (sie wird
  // nicht per CSS versteckt), damit kein leerer Rest stehen bleibt.
  //
  // 🔴 R16-B2: der Vergleich liest den ABONNIERTEN Zustand (s. o.), nicht
  // `istAusfall()` — nur so greift die Ausblendung auch, wenn der Ausfall
  // NACH dem Rendern bestaetigt wird, und die Erholung holt die Flaeche zurueck.
  //
  // 🔴 R16-W2-1 / R15-W1 (Nachbesserung 2, 2026-10-15): `showDuringOutage` ist
  // die OPT-IN-Ausnahme. Sie ist noetig, weil der Banner, der „fuer den
  // Fehlerzweig sprechen soll", bewusst UNTER jedem Modal liegt
  // (`ServiceOutageBanner.jsx` `bannerEbene` = `zIndex.modal - 1`); in einem
  // Overlay schweigt damit die eine Haelfte, waehrend die andere verdeckt ist.
  // Eine Flaeche, die den Ausfall selbst traegt, setzt die Prop und behaelt ihre
  // Aussage. Die Vorgabe `false` laesst JEDEN anderen Verbraucher unveraendert.
  if (reason === 'error' && zustand === ZUSTAND.AUSFALL && !showDuringOutage) return null;

  // R16-A W2 / R16-B W2: `error` ZUSAGT einen Weg heraus (siehe @useWhen), aber
  // der `action`-Slot ist optional — erzwingen laesst sich die Handlung nicht,
  // ohne jede bestehende Flaeche beim Einbau zu brechen. Was die Komponente
  // kann: es LAUT sagen. Eine Warnung im Entwicklungsmodus, KEIN Wurf — ein
  // Aufrufer ohne Handlung ist ein Mangel, aber kein Absturzgrund.
  //
  // 🔴 Neue Entwicklermeldungen sind ENGLISCH (UI2 Teil 10, G0.2): der
  // Sprachwaechter `check_german_words.sh` ist ein Ratchet und darf nur sinken —
  // jede neue deutsche Zeichenkette in dieser Datei wuerde ihn rot faerben.
  //
  // 🔴 R15-W4 / R16-W4 (Nachbesserung 1, 2026-10-11): der Text nennt BEIDE
  // Ausgaenge. Die Bedingung prueft `!action && !onRetry`, der Text sagte weiter
  // nur „ohne `action`" — die halbe Auskunft fuer den Aufrufer, der beides nicht
  // mitgibt. Genau die Sorte Zeile, die ihren Gegenstand ueberlebt.
  if (process.env.NODE_ENV !== 'production' && reason === 'error' && !action && !onRetry) {
    // eslint-disable-next-line no-console
    console.warn(
      'EmptyState: reason="error" ohne `action` und ohne `onRetry` — ein Fehler ohne Weg heraus ' +
      'sieht wie "nichts da" aus. Gib einen Wiederholen-/Retry-Weg mit.'
    );
  }

  // 🔴 R16-B1 (Nachbesserung 1, 2026-10-11): das Begriff-Echo haengt am GRUND.
  // Ein Aufrufer, der `query` gesetzt laesst, waehrend der Abruf scheitert,
  // bekaeme sonst „Konnte nicht geladen werden." UND „No case matches “kaffee”." —
  // die Flaeche sagt „nichts gefunden", wo „ich konnte nicht laden" gilt. Genau
  // der Zustand, gegen den dieser Baustein angetreten ist (@description). Also:
  // beide Zeilen rendern NUR bei `no-match`, und die falsche Kombination wird im
  // Entwicklungsmodus gemeldet — dieselbe Bauform wie oben (melden, nicht werfen).
  const isNoMatch = reason === 'no-match';
  // 🔴 Das Echo EXISTIERT nur bei `no-match` — nicht nur seine Zeile wird
  // gegattert. Sonst waere `echoIsMain` auch unter „error" wahr und das Echo
  // stuende als HAUPTZEILE ueber dem Fehlertext. Genau das hat die Probe
  // „R16-B1 — query mit reason=error" gefunden, bevor sie gruen war.
  const echo = isNoMatch && query
    ? (noun ? `No ${noun} matches “${query}”.` : `Nothing matches “${query}”.`)
    : null;

  if (process.env.NODE_ENV !== 'production' && !isNoMatch && (query || searchedIn)) {
    // eslint-disable-next-line no-console
    console.warn(
      'EmptyState: `query`/`searchedIn` render only for reason="no-match" — under ' +
      `"${reason}" the echo claims "nothing found" while the surface means something ` +
      'else. Drop it, or use reason="no-match".'
    );
  }

  // E.2 (G0.2): ein Begriff-Echo ohne Weg heraus ist der Zustand, gegen den die
  // Probe antritt. Dieselbe Bauform wie oben — melden, nicht werfen. Nur bei
  // `no-match`: sonst meldete eine Kombination, die gar kein Echo rendert.
  if (process.env.NODE_ENV !== 'production' && isNoMatch && query && !onClearSearch) {
    // eslint-disable-next-line no-console
    console.warn(
      'EmptyState: `query` without `onClearSearch` — a search echo with no way ' +
      'out. Pass onClearSearch, or drop the echo.'
    );
  }

  // Vier Anlässe, vier Vorgabetexte. Die Tonalität folgt dem Anlass, nicht dem
  // Geschmack: "noch nichts angelegt" ist ein Anfang, "nichts gefunden" ist
  // neutral, "konnte nicht laden" nennt den nächsten Schritt, "noch keine
  // Daten" sagt ausdrücklich, dass die leere Liste NICHTS bedeutet.
  //
  // 🔴 Die drei alten Vorgabetexte bleiben DEUTSCH und unverändert (UI2 Teil 10,
  // G0.2, W6). Eine Umstellung wäre nicht additiv; sie gehört in die
  // Ansichtskarten bzw. G16. Der neue vierte Text ist englisch — eine Fläche,
  // zwei Sprachen, und das ist hier ausdrücklich so.
  const DEFAULT = {
    'no-data':  'Hier ist noch nichts angelegt.',
    'no-match': 'Nichts passt zu dieser Auswahl.',
    'error':    'Konnte nicht geladen werden.',
    'unknown':  'No data from this source yet.',
  };

  const standardActions = Boolean(onClearSearch || onClearFilters || onRetry);

  // 🔴 R16-B2 (Nachbesserung 1, 2026-10-11): ohne `message` ist das Echo die
  // HAUPTZEILE. Sonst stuenden zwei Saetze untereinander, die dasselbe sagen —
  // „Nichts passt zu dieser Auswahl." (deutsch) und „No case matches “kaffee”."
  // (englisch). Konzept §8.1 fuehrt fuer diese Lage EINE Hauptzeile. Mit
  // `message` bleibt der Aufrufer Herr der Hauptzeile, das Echo steht darunter.
  const echoIsMain = Boolean(echo) && !message;

  return (
    // 🔴 R16-B-N2 (2026-10-05): `reason="error"` ist eine Live-Region. Die
    // Vereinheitlichung der vier Lieferantenflächen auf `EmptyState` hatte den
    // Fehlerzweig von MUI `Alert` (`role="alert"`, wird angesagt) auf diesen
    // Baustein umgestellt — der trug kein `role`, der Fehler wurde damit für
    // Hilfsmittel still. `role="alert"` ist `aria-live="assertive"`: ein Fehler,
    // der nach einem gescheiterten Abruf erscheint, gehört angesagt. Für
    // `no-data`/`no-match`/`unknown` bleibt es aus — das sind keine Unterbrechungen.
    //
    // 🔴 `data-reason` (G0.2) ist unbedingt und nicht sichtbar — siehe @description.
    // 🔴 `data-testid="leerzustand"` (S3-NB, 2026-11-03): `data-reason` ist kein
    //    Rollen- oder Textmerkmal, und Testing Library hat dafuer keine Abfrage.
    //    Bis dahin holten Proben den Wurzelknoten ueber `.closest('[data-reason]')`
    //    — direkter Knotenzugriff, den `testing-library/no-node-access` verbietet.
    //    Die Kennung ist der pruefbare Griff auf DENSELBEN Knoten; sie aendert
    //    weder Rolle, Text noch Layout. Sie ist nicht sichtbar und keine Prop.
    <Box
      data-reason={reason}
      data-testid="leerzustand"
      role={reason === 'error' ? 'alert' : undefined}
      sx={{ textAlign: 'center', py: SP.lg, px: SP.card }}
    >
      <Typography variant="body2" color="text.secondary">
        {message || (echoIsMain ? echo : DEFAULT[reason])}
      </Typography>
      {echo && !echoIsMain && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: SP.chip }}>
          {echo}
        </Typography>
      )}
      {isNoMatch && searchedIn && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: SP.chip }}>
          {`Searched in: ${searchedIn}`}
        </Typography>
      )}
      {detail && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: SP.chip }}>
          {detail}
        </Typography>
      )}
      {action && <Box sx={{ mt: SP.card }}>{action}</Box>}
      {/* Die drei Standardhandlungen stehen in einem EIGENEN Block: der
          `action`-Slot darüber bleibt damit Zeichen fuer Zeichen die alte Form,
          und "ohne die neuen Props" ist keine Behauptung, sondern der Zustand. */}
      {standardActions && (
        <Box
          sx={{
            mt: SP.card,
            display: 'flex',
            gap: SP.tight,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {onClearSearch && (
            <Button onClick={onClearSearch}>Clear search</Button>
          )}
          {onClearFilters && (
            <Button onClick={onClearFilters}>Clear all filters</Button>
          )}
          {/* 🔴 R16-W1 (Nachbesserung 1, 2026-10-11): `outlined` wie die Mehrheit
              der Bestands-`action`-Slots. Dieselbe Beschriftung darf nicht zwei
              Erscheinungen haben (unter 600 px wird der umrandete Knopf zur
              Glas-Pille, der Textknopf nicht). Gilt NUR fuer den eingebauten
              Knopf — der `action`-Slot des Aufrufers bleibt unangetastet.
              „Clear search" und „Clear all filters" bleiben Textknoepfe: sie sind
              das leichtere Paar, und drei Glas-Pillen nebeneinander waeren schwer. */}
          {onRetry && (
            <Button variant="outlined" onClick={onRetry}>Try again</Button>
          )}
        </Box>
      )}
    </Box>
  );
}
